/**
 * AppLayout
 *
 * Pure layout composition component that provides the base structure for application layouts.
 * This component wraps the sidebar registry components with proper positioning to work with
 * AppHeader and other top-level UI elements.
 *
 * Key Features:
 * - Fixes sidebar positioning to work below AppHeader (prevents inset-y-0 viewport issues)
 * - Provides proper spacing and overflow handling
 * - Supports both docked and floating sidebar variants
 * - Pure composition - no business logic
 *
 * @example
 * ```tsx
 * <AppLayout
 *   sidebarVariant="docked"
 *   header={<AppHeader {...headerProps} />}
 * >
 *   <YourContent />
 * </AppLayout>
 * ```
 */

import * as React from "react"
import { cn } from "@shadcn/lib/utils"
import { SidebarProvider } from "@shadcn/components/layouts/Sidebar/Sidebar"

export interface AppLayoutProps {
	/**
	 * Main content area
	 */
	children: React.ReactNode
	/**
	 * Optional header to display above the sidebar/content area
	 */
	header?: React.ReactNode
	/**
	 * Sidebar variant from registry component
	 */
	sidebarVariant?: "docked" | "floating"
	/**
	 * Default sidebar open state
	 */
	defaultSidebarOpen?: boolean
	/**
	 * Additional classes for the root container
	 */
	className?: string
}

/**
 * AppLayout wraps the SidebarProvider and provides the base structure.
 *
 * The key difference from using SidebarProvider directly:
 * 1. Wraps everything in a proper flex container
 * 2. Prevents sidebar from using viewport-based positioning (inset-y-0)
 * 3. Provides spacing that matches PageTemplate design
 * 4. Works correctly with AppHeader positioned above
 */
export function AppLayout({
	children,
	header,
	sidebarVariant = "docked",
	defaultSidebarOpen = true,
	className,
}: Readonly<AppLayoutProps>) {
	return (
		<div className="h-screen w-full flex flex-col overflow-hidden">
			{/* Header Area - Full Width */}
			{header && <div className="flex-none">{header}</div>}

			{/* Sidebar + Content Area - Below Header */}

			<SidebarProvider
				defaultOpen={defaultSidebarOpen}
				className={cn(
					// Removed fixed height for responsiveness; min-h ensures adequate space.
					"flex min-h-[600px] w-full overflow-hidden"
				)}>
				{/*
						Children should include:
						1. A <Sidebar> component with your nav items
						2. Content wrapped in <SidebarInset> or similar
					*/}
				<div className="flex w-full h-full">{children}</div>
			</SidebarProvider>
		</div>
	)
}

export default AppLayout
